// apps/app/src/lib/auth.ts

import { create } from 'zustand';
import { ApiError, http, refreshSession, setAccessToken, onSignedOut } from './api-client';

export type Role = 'PLATFORM' | 'OWNER' | 'ADMIN' | 'PRACTITIONER' | 'FRONT_DESK';
export type TenantState = 'TEST' | 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'DORMANT' | 'CANCELLED';

export interface SessionUser {
  id: string;
  email: string;
  role: Role;
  /** Null for PLATFORM staff, who are not attached to a clinic. */
  tenantId: string | null;
  tenantSlug: string | null;
  tenantState: TenantState | null;
}

interface AuthState {
  user: SessionUser | null;
  /**
   * `unavailable`: the session could not be checked because the API could not
   * be reached. Not the same as signed out, and never shown as the login page.
   */
  status: 'loading' | 'authenticated' | 'anonymous' | 'unavailable';
  login(email: string, password: string): Promise<void>;
  register(input: RegisterInput): Promise<void>;
  logout(): Promise<void>;
  restore(): Promise<void>;
}

export interface RegisterInput {
  email: string;
  password: string;
  clinicName: string;
  country: string;
  timezone: string;
  currency: string;
  acceptedDpa: true;
}

interface AuthResponse {
  accessToken: string;
  expiresIn: number;
  user: SessionUser;
}

/**
 * In flight, so concurrent callers share one attempt. Without this, React's
 * StrictMode double-invoke turned a cold load into several parallel refreshes,
 * each of which rotates the token — and a rotated token being presented twice
 * is exactly what reuse detection revokes a session for.
 */
let restoring: Promise<void> | null = null;

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'loading',

  async login(email, password) {
    const result = await http.post<AuthResponse>('/auth/login', { email, password });
    setAccessToken(result.accessToken);
    set({ user: result.user, status: 'authenticated' });
  },

  async register(input) {
    const result = await http.post<AuthResponse>('/auth/register', input);
    setAccessToken(result.accessToken);
    set({ user: result.user, status: 'authenticated' });
  },

  async logout() {
    await http.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    set({ user: null, status: 'anonymous' });
  },

  /**
   * Runs once on boot. The access token is in memory, so a page reload always
   * starts from the refresh cookie — a silent refresh is the normal path here,
   * not an error case.
   */
  async restore() {
    if (restoring) return restoring;

    restoring = (async () => {
      set({ status: 'loading' });
      try {
        const outcome = await refreshSession();
        if (outcome === 'invalid') {
          set({ user: null, status: 'anonymous' });
          return;
        }
        if (outcome === 'unavailable') {
          set({ status: 'unavailable' });
          return;
        }

        // One call, and the real role. This previously asked for `/users/me` —
        // which does not exist — then fell back to a hardcoded OWNER, so after
        // a reload a front-desk user was shown the whole dashboard and
        // collected 403s. It also required `/tenants/me`, which platform staff
        // cannot read, so an operator was signed out on every refresh.
        const user = await http.get<SessionUser>('/auth/me');
        set({ user, status: 'authenticated' });
      } catch (e) {
        // Only the server saying no ends a session. A busy or unreachable API
        // previously signed people out of a perfectly valid one.
        const refused = e instanceof ApiError && (e.status === 401 || e.status === 403);
        set(refused ? { user: null, status: 'anonymous' } : { status: 'unavailable' });
      } finally {
        restoring = null;
      }
    })();

    return restoring;
  },
}));

// A revoked refresh family — reuse detection firing — signs the tab out
// immediately rather than leaving it showing stale data it can no longer refetch.
onSignedOut(() => {
  setAccessToken(null);
  useAuth.setState({ user: null, status: 'anonymous' });
});
